import React, { useEffect, useState } from "react";
import NavTabs from "../../../shared/NavTabs";
import * as api from "../../../../backend/request";
import dayjs from "dayjs";
import ActivityList from "./ActivityList";
import "./dashboard.css";
import { navigate } from "raviger";
import { preventDefault } from "../../../../utils/common";
import { getBasePath } from "../../../../App";
import { ACTIVITY_TYPE } from "../../../../utils/constants";
import { useGetAllActivities } from "../../../../backend/query";

const TodosTabs = [
    { value: 'Today', label: 'Today' },
    { value: 'ThisWeek', label: 'This Week' },
    { value: 'ThisMonth', label: 'This Month' }
];

const UpcomingTabs = [
    { value: 'NextWeek', label: 'Next Week' },
    { value: 'NextMonth', label: 'Next Month' },
    { value: 'NextQuarter', label: 'Next Quarter' }
];

function Todo({ upcoming, selectedCompany, selectedAssociateCompany, selectedLocation }: any) {
    const [title] = useState(upcoming ? 'Upcoming' : 'To-Do');
    const [tabs] = useState(upcoming ? UpcomingTabs : TodosTabs);
    const [frequency, setFrequency] = useState(upcoming ? UpcomingTabs[0].value : TodosTabs[0].value);
    const [activitiesPayload, setActivitiesPayload] = useState<any>({})
    const [count, setCount] = useState<number | null>(null);
    const [label, setLabel] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<any>({})
    const { activities, total, isFetching, refetch } = useGetAllActivities(activitiesPayload, Boolean(hasFilters(activitiesPayload, 'company_id_eq')));

    function hasFilters({filters}: any = {}, field = 'location_id_eq') {
        return Boolean((filters || {})[field])
    }

    function updateActivities(dateRangeFilter = {}) {
        setCount(null);
        setLabel(null);
        if (selectedCompany && selectedAssociateCompany && selectedLocation) {
            const requestPayload = {
                filters: {
                    company_id_eq: selectedCompany,
                    associate_company_id_eq: selectedAssociateCompany,
                    location_id_eq: selectedLocation,
                    ...dateRangeFilter
                },
                page: 1,
                per: 4,
                includes: ['act', 'rule', 'activity', 'location', 'company', 'associate_company'],
                include: ['act', 'rule', 'activity', 'location', 'company', 'associate_company']
            };
            setActivitiesPayload(requestPayload);
        }
    }

    function viewAll(e: any) {
        preventDefault(e);
        navigate(`${getBasePath()}/dashboard/activities`, {
            state: {
                company: selectedCompany,
                associate_company: selectedAssociateCompany,
                location: selectedLocation,
                ...dateRange,
                auditType: ACTIVITY_TYPE.AUDIT
            }
        });
    }

    const buildDateQuery = (frequency: string) => {
        const today = new Date();

        const formatDate = (date: Date) => {
          return date.toISOString().split('T')[0] + ' ' + date.toTimeString().split(' ')[0];
        };
        switch (frequency) {
          case 'today':
            const startOfDay = new Date(today.setHours(0, 0, 0, 0));
            return {
              due_date_eq: formatDate(startOfDay),
            };

          case 'thisweek':
            const monday = new Date(today);
            monday.setDate(today.getDate() - today.getDay() + 1);
            monday.setHours(0, 0, 0, 0);

            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            sunday.setHours(23, 59, 59, 999);

            return {
              due_date_gteq: formatDate(monday),
              due_date_lteq: formatDate(sunday)
            };

          case 'thismonth':
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            endOfMonth.setHours(23, 59, 59, 999);

            return {
              due_date_gteq: formatDate(startOfMonth),
              due_date_lteq: formatDate(endOfMonth)
            };

          case 'nextweek':
            const nextMonday = new Date(today);
            nextMonday.setDate(today.getDate() - today.getDay() + 8);
            nextMonday.setHours(0, 0, 0, 0);

            const nextSunday = new Date(nextMonday);
            nextSunday.setDate(nextMonday.getDate() + 6);
            nextSunday.setHours(23, 59, 59, 999);

            return {
              due_date_gteq: formatDate(nextMonday),
              due_date_lteq: formatDate(nextSunday)
            };

          case 'nextmonth':
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
            endOfNextMonth.setHours(23, 59, 59, 999);

            return {
                due_date_gteq: formatDate(nextMonth),
                due_date_lteq: formatDate(endOfNextMonth)
            };

          case 'nextquarter':
            const currentMonth = today.getMonth();
            let quarterStart;

            if (currentMonth < 3) quarterStart = new Date(today.getFullYear(), 3, 1);
            else if (currentMonth < 6) quarterStart = new Date(today.getFullYear(), 6, 1);
            else if (currentMonth < 9) quarterStart = new Date(today.getFullYear(), 9, 1);
            else quarterStart = new Date(today.getFullYear() + 1, 0, 1);

            const quarterEnd = new Date(quarterStart);
            quarterEnd.setMonth(quarterStart.getMonth() + 3);
            quarterEnd.setDate(0);
            quarterEnd.setHours(23, 59, 59, 999);

            return {
                due_date_gteq: formatDate(quarterStart),
                due_date_lteq: formatDate(quarterEnd)
            };

          default:
            return {};
        }
    };

    useEffect(() => {
        if (selectedCompany && selectedAssociateCompany && selectedLocation) {
            setFrequency(upcoming ? UpcomingTabs[0].value : TodosTabs[0].value)
        }
    }, [selectedCompany, selectedAssociateCompany, selectedLocation]);

    useEffect(() => {
        if(activities) {
            setCount(total)
        }
    }, [activities])

    useEffect(() => {
        if(frequency) {

            const dateRange: any = buildDateQuery(frequency.toLocaleLowerCase())

            const label = frequency !== 'Today' ?
                `${dayjs(dateRange.start_date_gteq).format('DD-MMM-YYYY')} - ${dayjs(dateRange.start_date_lteq).format('DD-MMM-YYYY')}` :
                `${dayjs(dateRange.start_date_gteq).format('DD-MMM-YYYY')}`;
            setLabel(label);
            setDateRange(dateRange);
            updateActivities(dateRange);
        }

    }, [frequency])

    return (
        <>
            <div className="card todo-card shadow">
                <div className="card-header bg-white border-0 underline text-appprimary fw-semibold fs-5 d-flex align-items-center">
                    <div>
                        {
                            upcoming ?
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 3H21C21.2652 3 21.5196 3.10536 21.7071 3.29289C21.8946 3.48043 22 3.73478 22 4V20C22 20.2652 21.8946 20.5196 21.7071 20.7071C21.5196 20.8946 21.2652 21 21 21H3C2.73478 21 2.48043 20.8946 2.29289 20.7071C2.10536 20.5196 2 20.2652 2 20V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3H7V1H9V3H15V1H17V3ZM4 9V19H20V9H12H4Z" fill="#2965AD" />
                                    <path d="M8.69835 17L12.0272 14L8.69835 11L8 11.6294L10.6305 14L8 16.3706L8.69835 17Z" fill="#2965AD" />
                                    <path d="M16 14L12.6712 11L11.9728 11.6294L14.6033 14L11.9728 16.3706L12.6712 17L16 14Z" fill="#2965AD" />
                                </svg>
                                :
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 3H21C21.2652 3 21.5196 3.10536 21.7071 3.29289C21.8946 3.48043 22 3.73478 22 4V20C22 20.2652 21.8946 20.5196 21.7071 20.7071C21.5196 20.8946 21.2652 21 21 21H3C2.73478 21 2.48043 20.8946 2.29289 20.7071C2.10536 20.5196 2 20.2652 2 20V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3H7V1H9V3H15V1H17V3ZM4 9V19H20V9H4ZM6 11H8V13H6V11ZM6 15H8V17H6V15ZM10 11H18V13H10V11ZM10 15H15V17H10V15Z" fill="#2965AD" />
                                </svg>
                        }
                    </div>
                    <div className="mx-2">
                        {title}
                    </div>
                </div>
                <div className="card-body pt-1">
                    <h5 className="text-center mb-3 fw-semibold">
                        <a href="/" onClick={preventDefault}>{count || 0} Activities</a>
                    </h5>
                    {
                        tabs &&
                        <NavTabs list={tabs} onTabChange={(tab: string) => setFrequency(tab)} />
                    }
                    <div className="tab-content">
                        <div className="tab-pane fade show active" role="tabpanel">
                            <div className="my-3">
                                <div className="text-center mb-3 dashboard-date-range-label">
                                    {label && <strong className="text-primary">({label})</strong>}
                                </div>

                                <div className="row m-0 card cardList border-0">
                                    <div className="card-body p-0" >
                                        <ActivityList list={activities} />
                                        <div className="text-primary d-flex justify-content-end fw-bold position-absolute" style={{ right: '1rem' }}>
                                            {
                                                activities.length > 0 &&
                                                <a href="/" onClick={viewAll}>View All</a>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Todo;
